import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Row,
  Col,
  Input,
  Table,
  Tag,
  Typography,
  Statistic,
  Space,
  Button,
  Tooltip,
  Collapse,
  Avatar,
  Badge,
  Empty,
  Skeleton,
  message,
} from 'antd';
import {
  TeamOutlined,
  GiftOutlined,
  UserOutlined,
  SearchOutlined,
  CopyOutlined,
  DownOutlined,
  RightOutlined,
  PhoneOutlined,
  MailOutlined,
  DownloadOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/fr';
import referralService, { Referrer, ReferredCustomer } from '../services/referralService';
import { exportToCSV } from '../utils/exportUtils';

dayjs.extend(relativeTime);
dayjs.locale('fr');

const { Title, Text } = Typography;
const { Search } = Input;
const { Panel } = Collapse;

const ReferralPage: React.FC = () => {
  const [referrers, setReferrers] = useState<Referrer[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [stats, setStats] = useState({ total_referrers: 0, total_referred: 0 });
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });

  const fetchData = useCallback(async (page = 1, searchVal = '') => {
    setLoading(true);
    try {
      const res = await referralService.getReferrals({
        page,
        limit: 20,
        search: searchVal || undefined,
      });
      setReferrers(res.data);
      setStats(res.stats);
      setPagination((prev) => ({
        ...prev,
        current: res.pagination.page,
        total: res.pagination.total,
      }));
    } catch (err) {
      message.error('Impossible de charger les parrainages');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(1, '');
  }, [fetchData]);

  const handleSearch = (value: string) => {
    setSearch(value);
    fetchData(1, value);
  };

  const copyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    message.success(`Code "${code}" copié !`);
  };

  // Colonnes du tableau des filleuls (dans le panel expandable)
  const referralColumns = [
    {
      title: 'Filleul',
      key: 'name',
      render: (_: any, r: ReferredCustomer) => (
        <Space>
          <Avatar size="small" icon={<UserOutlined />} style={{ background: '#1677ff' }} />
          <Text strong>{r.first_name} {r.last_name}</Text>
        </Space>
      ),
    },
    {
      title: 'Son code parrain',
      key: 'referral_code',
      render: (_: any, r: ReferredCustomer) =>
        r.referral_code ? (
          <Space>
            <Tag color="blue" style={{ fontFamily: 'monospace', letterSpacing: 0.5 }}>
              {r.referral_code}
            </Tag>
            <Tooltip title="Copier le code">
              <Button
                size="small"
                type="text"
                icon={<CopyOutlined />}
                onClick={() => copyCode(r.referral_code!)}
              />
            </Tooltip>
          </Space>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: 'Contact',
      key: 'contact',
      render: (_: any, r: ReferredCustomer) => (
        <Space direction="vertical" size={0}>
          {r.phone && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              <PhoneOutlined /> {r.phone}
            </Text>
          )}
          {r.email && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              <MailOutlined /> {r.email}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Inscrit le',
      key: 'joined_at',
      render: (_: any, r: ReferredCustomer) => (
        <Tooltip title={dayjs(r.joined_at).format('DD/MM/YYYY HH:mm')}>
          <Text type="secondary">{dayjs(r.joined_at).fromNow()}</Text>
        </Tooltip>
      ),
    },
  ];

  // Colonnes du tableau principal (parrains)
  const columns = [
    {
      title: 'Parrain',
      key: 'referrer',
      render: (_: any, r: Referrer) => (
        <Space>
          <Avatar
            style={{ background: '#0a543d', fontWeight: 700 }}
            size={36}
          >
            {r.first_name.charAt(0)}{r.last_name.charAt(0)}
          </Avatar>
          <div>
            <Text strong style={{ display: 'block' }}>
              {r.first_name} {r.last_name}
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {r.phone || r.email || '—'}
            </Text>
          </div>
        </Space>
      ),
    },
    {
      title: 'Code parrainage',
      dataIndex: 'referral_code',
      key: 'referral_code',
      render: (code: string) => (
        <Space>
          <Tag
            color="green"
            style={{ fontFamily: 'monospace', fontSize: 13, letterSpacing: 0.5, padding: '2px 10px' }}
          >
            {code}
          </Tag>
          <Tooltip title="Copier">
            <Button
              size="small"
              type="text"
              icon={<CopyOutlined />}
              onClick={() => copyCode(code)}
            />
          </Tooltip>
        </Space>
      ),
    },
    {
      title: 'Filleuls',
      key: 'referrals_count',
      sorter: (a: Referrer, b: Referrer) => a.referrals_count - b.referrals_count,
      render: (_: any, r: Referrer) => (
        <Badge
          count={r.referrals_count}
          showZero
          style={{
            background: r.referrals_count > 0 ? '#0a543d' : '#d9d9d9',
            fontWeight: 700,
          }}
        />
      ),
    },
    {
      title: 'Membre depuis',
      dataIndex: 'created_at',
      key: 'created_at',
      sorter: (a: Referrer, b: Referrer) =>
        dayjs(a.created_at).unix() - dayjs(b.created_at).unix(),
      render: (date: string) => (
        <Tooltip title={dayjs(date).format('DD/MM/YYYY')}>
          <Text type="secondary">{dayjs(date).fromNow()}</Text>
        </Tooltip>
      ),
    },
  ];

  const [exporting, setExporting] = useState(false);

  const handleExport = () => {
    try {
      setExporting(true);
      const rows: any[] = [];

      (referrers || []).forEach(r => {
        const parrainName = `${r.first_name || ''} ${r.last_name || ''}`.trim();
        const referredCustomers = (r as any).referred_customers || [];
        if (referredCustomers && referredCustomers.length > 0) {
          referredCustomers.forEach((f: any) => {
            rows.push({
              parrain: parrainName,
              telephone_parrain: r.phone || '',
              email_parrain: r.email || '',
              code_parrainage: r.referral_code,
              nombre_filleuls: r.referrals_count,
              filleul: `${f.first_name || ''} ${f.last_name || ''}`.trim(),
              telephone_filleul: f.phone || '',
              email_filleul: f.email || '',
              date_inscription_filleul: f.joined_at ? dayjs(f.joined_at).format('DD/MM/YYYY HH:mm') : ''
            });
          });
        } else {
          rows.push({
            parrain: parrainName,
            telephone_parrain: r.phone || '',
            email_parrain: r.email || '',
            code_parrainage: r.referral_code,
            nombre_filleuls: r.referrals_count,
            filleul: 'Aucun filleul',
            telephone_filleul: '-',
            email_filleul: '-',
            date_inscription_filleul: '-'
          });
        }
      });

      const headers = [
        { header: 'Parrain', key: 'parrain' },
        { header: 'Téléphone Parrain', key: 'telephone_parrain' },
        { header: 'Email Parrain', key: 'email_parrain' },
        { header: 'Code Parrainage', key: 'code_parrainage' },
        { header: 'Total Filleuls', key: 'nombre_filleuls' },
        { header: 'Filleul', key: 'filleul' },
        { header: 'Téléphone Filleul', key: 'telephone_filleul' },
        { header: 'Email Filleul', key: 'email_filleul' },
        { header: 'Date Inscription Filleul', key: 'date_inscription_filleul' },
      ];

      exportToCSV(rows, headers, `parrainages_${new Date().toISOString().slice(0, 10)}.csv`);
      message.success('Export des parrainages généré avec succès');
    } catch (err) {
      console.error('Erreur export parrainage:', err);
      message.error("Erreur lors de l'export des parrainages");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div style={{ padding: '0 0 32px' }}>
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={4} style={{ margin: 0, color: '#0a543d' }}>
            <GiftOutlined style={{ marginRight: 8 }} />
            Gestion du Parrainage
          </Title>
          <Text type="secondary">
            Visualisez les parrains, leurs codes, et les filleuls qu'ils ont recrutés
          </Text>
        </Col>
        <Col>
          <Space>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleExport}
              loading={exporting}
            >
              Exporter
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => fetchData(pagination.current, search)}
              loading={loading}
            >
              Actualiser
            </Button>
          </Space>
        </Col>
      </Row>

      {/* KPI Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12}>
          <Card
            style={{
              borderRadius: 12,
              border: '1px solid #E2E8F0',
              background: '#FFFFFF',
              boxShadow: '0 4px 6px -1px rgba(0,0,0,0.03)',
            }}
          >
            {loading ? (
              <Skeleton active paragraph={{ rows: 1 }} />
            ) : (
              <>
                <Statistic
                  title={<Text style={{ color: '#475569', fontWeight: 600 }}>Parrains actifs</Text>}
                  value={stats.total_referrers}
                  prefix={<GiftOutlined style={{ color: '#0A543D', marginRight: 8 }} />}
                  valueStyle={{ color: '#0F172A', fontWeight: 700, fontSize: 32 }}
                />
                <Text style={{ fontSize: 12, color: '#64748B' }}>
                  Clients disposant d'un code de parrainage actif
                </Text>
              </>
            )}
          </Card>
        </Col>
        <Col xs={24} sm={12}>
          <Card
            style={{
              borderRadius: 12,
              border: '1px solid #E2E8F0',
              background: '#FFFFFF',
              boxShadow: '0 4px 6px -1px rgba(0,0,0,0.03)',
            }}
          >
            {loading ? (
              <Skeleton active paragraph={{ rows: 1 }} />
            ) : (
              <>
                <Statistic
                  title={<Text style={{ color: '#475569', fontWeight: 600 }}>Total filleuls recrutés</Text>}
                  value={stats.total_referred}
                  prefix={<TeamOutlined style={{ color: '#EEBD1B', marginRight: 8 }} />}
                  valueStyle={{ color: '#0F172A', fontWeight: 700, fontSize: 32 }}
                />
                <Text style={{ fontSize: 12, color: '#64748B' }}>
                  Clients inscrits et confirmés via parrainage
                </Text>
              </>
            )}
          </Card>
        </Col>
      </Row>

      {/* Tableau principal */}
      <Card
        style={{ borderRadius: 12, border: '1px solid #E2E8F0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.03)' }}
        title={
          <Row align="middle" justify="space-between" gutter={[8, 8]}>
            <Col>
              <Text strong style={{ color: '#0F172A', fontSize: 16 }}>Liste des parrains</Text>
            </Col>
            <Col xs={24} sm={10} md={8}>
              <Search
                placeholder="Rechercher par nom ou code..."
                allowClear
                onSearch={handleSearch}
                onChange={(e) => !e.target.value && handleSearch('')}
                prefix={<SearchOutlined style={{ color: '#94A3B8' }} />}
              />
            </Col>
          </Row>
        }
      >
        {loading && referrers.length === 0 ? (
          <Skeleton active paragraph={{ rows: 8 }} />
        ) : (
          <Table<Referrer>
            dataSource={referrers}
            columns={columns}
            rowKey="id"
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              onChange: (page) => fetchData(page, search),
              showTotal: (total) => `${total} parrain(s)`,
            }}
            expandable={{
              expandedRowRender: (record: Referrer) =>
                record.referrals.length === 0 ? (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="Aucun filleul pour ce parrain"
                    style={{ margin: '16px 0' }}
                  />
                ) : (
                  <div style={{ padding: '8px 0 8px 48px' }}>
                    <Text type="secondary" style={{ marginBottom: 8, display: 'block' }}>
                      {record.referrals.length} filleul(s) recruté(s) avec le code{' '}
                      <Tag color="green">{record.referral_code}</Tag>
                    </Text>
                    <Table<ReferredCustomer>
                      dataSource={record.referrals}
                      columns={referralColumns}
                      rowKey="id"
                      size="small"
                      pagination={false}
                      style={{ background: '#F8FAFC', borderRadius: 8 }}
                    />
                  </div>
                ),
              expandIcon: ({ expanded, onExpand, record }) =>
                record.referrals_count > 0 ? (
                  <Button
                    type="text"
                    size="small"
                    icon={expanded ? <DownOutlined /> : <RightOutlined />}
                    onClick={(e) => onExpand(record, e)}
                  />
                ) : null,
            }}
            locale={{ emptyText: 'Aucun parrain trouvé' }}
          />
        )}
      </Card>
    </div>
  );
};

export default ReferralPage;
